import React, {useMemo} from 'react'
import {Box, Flex} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../components/cms/slider'
import ModuleContainer from '../../components/module-container'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'
import Fade from '../../components/cms/fade'
import {hasValidString} from '../../utils/utils'
import CTA from '../../components/cms/cta'
import {useSlotRecipe} from '@chakra-ui/react3'
import {productCarouselRecipe} from './index.recipe'
import {useIntl} from 'react-intl'
import {MODULE_TYPE_LABELS, MODULE_TYPES} from '../constants'
import ProductTile from '../../../components/product-tile'
import {isProductOrderable, isProductSet} from '../../../utils/product-utils'

const ProductCarousel = React.forwardRef(function Carousel(
    {
        moduleId,
        moduleType,
        pageId,
        headingLevel,
        alignment,
        heading,
        copy,
        tileProducts,
        anchor,
        background,
        spacing,
        cta,
        reverseImageOrder,
        showPayLater
    },
    ref
) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: productCarouselRecipe})
    const styles = useMemo(() => recipe({alignment}), [recipe, alignment])

    const ariaLabel = useMemo(
        () =>
            [
                hasValidString(heading.subline) && `${heading.subline} -`,
                heading.headline1,
                heading.headline2
            ]
                .filter(hasValidString)
                .join(' '),
        [heading.subline, heading.headline1, heading.headline2]
    )
    const visibleProducts = useMemo(
        () =>
            tileProducts?.data?.filter(
                (p) => p.productExists !== false && (isProductSet(p) || isProductOrderable(p))
            ),
        [tileProducts]
    )

    if (!visibleProducts?.length) {
        return null
    }

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            ref={ref}
            {...styles.moduleContainer}
        >
            <Flex {...styles.headerContainer}>
                <Flex {...styles.headingCopyContainer}>
                    <Fade>
                        <Heading
                            {...heading}
                            headingLevel={headingLevel}
                            alignment={alignment}
                            type={formatMessage(MODULE_TYPE_LABELS[MODULE_TYPES.CAROUSEL])}
                        />
                    </Fade>
                    {hasValidString(copy?.text) && (
                        <Fade>
                            <Copy {...copy} alignment={alignment} />
                        </Fade>
                    )}
                </Flex>
                {cta?.label && (
                    <Box {...styles.ctaContainer}>
                        <CTA
                            {...cta}
                            variant={cta.variant === 'secondary' ? 'secondaryGhost' : cta.variant}
                            trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                        />
                    </Box>
                )}
            </Flex>

            <Box {...styles.sliderContainer}>
                <Slider sliderLabel={ariaLabel} variant="productCarousel">
                    {visibleProducts.map((product, index) => (
                        <ProductTile
                            key={index}
                            product={product}
                            variant="leap-reco"
                            reverseImageOrder={reverseImageOrder}
                            payLaterVariant={showPayLater}
                        />
                    ))}
                </Slider>
            </Box>
        </ModuleContainer>
    )
})

ProductCarousel.propTypes = {
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    hideHeadline: PropTypes.bool,
    alignment: PropTypes.oneOf(['left', 'center']),
    copy: PropTypes.object,
    tiles: PropTypes.arrayOf(
        PropTypes.shape({
            productTile: PropTypes.shape({
                productId: PropTypes.string
            })
        })
    ),
    tileProducts: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object)
    }),
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    anchor: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    cta: PropTypes.object,
    reverseImageOrder: PropTypes.bool,
    showPayLater: PropTypes.oneOf(['none', 'klarna', 'paypal'])
}

export default ProductCarousel
